import type { ClinicBranding, UpdateBranding } from '@dnx/contracts';
import { Alert, Badge, Button, Card, CardHeader, Field, Input, brandTheme } from '@dnx/ui';
import { useMutation } from '@tanstack/react-query';
import { CircleCheck, ImageUp, KeyRound, Trash2 } from 'lucide-react';
import { type CSSProperties, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api.js';

const HEX = /^#[0-9A-Fa-f]{6}$/;

/** Campo de cor: o id do <label> vai para o campo de texto (o seletor nativo é um atalho visual). */
function ColorInputs({ value, onChange, optional, label, ...aria }: {
  value: string; onChange: (v: string) => void; optional?: boolean; label: string;
  id?: string; 'aria-describedby'?: string; invalid?: boolean;
}) {
  const { invalid, ...rest } = aria;
  return (
    <div className="flex gap-2">
      <span className="relative h-10 w-12 shrink-0">
        <input type="color" aria-label={`${label}: seletor de cor`} value={HEX.test(value) ? value : '#FFFFFF'}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className={`size-full cursor-pointer rounded-md border border-border-strong bg-surface p-1 ${HEX.test(value) ? '' : 'opacity-0'}`} />
        {!HEX.test(value) && (
          <span aria-hidden className="pointer-events-none absolute inset-0 rounded-md border border-dashed border-border-strong bg-[repeating-linear-gradient(45deg,transparent_0_4px,var(--dnx-border)_4px_5px)]" />
        )}
      </span>
      <Input {...rest} invalid={invalid} className="font-mono uppercase" value={value} maxLength={7} placeholder={optional ? 'sem cor' : '#RRGGBB'}
        onChange={(e) => onChange(e.target.value.startsWith('#') || e.target.value === '' ? e.target.value : `#${e.target.value}`)} />
    </div>
  );
}

function ColorField({ label, value, onChange, optional }: { label: string; value: string; onChange: (v: string) => void; optional?: boolean }) {
  const valid = HEX.test(value) || (optional && value === '');
  return (
    <Field label={label} error={valid ? undefined : 'Use o formato #RRGGBB'} hint={optional ? 'Opcional: detalhes e destaques.' : undefined}>
      <ColorInputs label={label} value={value} onChange={onChange} optional={optional} />
    </Field>
  );
}

/** Pré-visualização do portal com as variáveis da marca aplicadas só neste container. */
function PortalPreview({ name, primary, logoUrl }: { name: string; primary: string; logoUrl: string | null }) {
  const theme = useMemo(() => brandTheme(HEX.test(primary) ? primary : '#0F4C5C'), [primary]);
  const initials = name.replace(/^(cl[ií]nica|instituto|centro)\s+/i, '').split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]!.toUpperCase()).join('');
  return (
    <div className="space-y-3">
      <div style={theme.vars as CSSProperties} className="mx-auto w-[260px] overflow-hidden rounded-[28px] border-[6px] border-fg/85 bg-bg shadow-pop">
        <div className="px-5 pt-7 pb-8">
          <div className="flex items-center gap-2.5">
            {logoUrl
              ? <img src={logoUrl} alt="" className="size-9 rounded-xl object-contain" />
              : <span className="grid size-9 place-items-center rounded-xl bg-primary text-[13px] font-bold text-primary-fg">{initials || '?'}</span>}
            <span className="truncate text-[14px] font-semibold text-fg">{name || 'Nome da clínica'}</span>
          </div>
          <p className="mt-7 text-[17px] leading-tight font-semibold text-fg">Sua área do paciente</p>
          <p className="mt-1 text-[11px] text-fg-muted">Acompanhe suas consultas, fotos e orientações.</p>
          <div className="mt-5 h-9 rounded-md border border-primary bg-surface px-3 text-[12px] leading-9 text-fg-subtle">nome@exemplo.com</div>
          <div className="mt-2 flex h-9 items-center justify-center gap-2 rounded-md border border-border bg-surface text-[13px] tracking-[0.3em] text-fg-subtle">••••••</div>
          <div className="mt-3 flex h-9 items-center justify-center gap-1.5 rounded-md bg-primary text-[12px] font-medium text-primary-fg">
            <KeyRound className="size-3.5" />Entrar
          </div>
          <p className="mt-4 text-center text-[11px] font-medium text-primary">Link de exemplo</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-2 text-xs">
        <Badge tone={theme.contrastOnWhite >= 7 ? 'success' : 'primary'}>
          <CircleCheck className="size-3" aria-hidden />Contraste {theme.contrastOnWhite.toFixed(1)}:1 · {theme.contrastOnWhite >= 7 ? 'AAA' : 'AA'}
        </Badge>
      </div>
      {theme.adjusted && (
        <Alert tone="warning" title="Tom ajustado automaticamente">
          <span className="flex items-start gap-1.5">A cor escolhida é clara demais para texto branco. O portal usa um tom um pouco mais escuro, com o mesmo matiz, para garantir leitura.</span>
        </Alert>
      )}
    </div>
  );
}

const LOGO_MAX = 512 * 1024;
const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

/** Logo: PNG, JPEG ou WebP até 512 KB (o servidor confere de novo pelos bytes do arquivo). */
function LogoField({ endpoint, logoUrl, onChange }: { endpoint: string; logoUrl: string | null; onChange: (b: ClinicBranding) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const upload = useMutation({
    mutationFn: (image: string) => api<ClinicBranding>(`${endpoint}/logo`, { method: 'PUT', body: { image } }),
    onSuccess: onChange,
  });
  const remove = useMutation({ mutationFn: () => api<ClinicBranding>(`${endpoint}/logo`, { method: 'DELETE' }), onSuccess: onChange });
  const pick = (file: File | undefined) => {
    setLocalError(null);
    if (!file) return;
    if (!LOGO_TYPES.includes(file.type)) { setLocalError('Use PNG, JPEG ou WebP. SVG não é aceito.'); return; }
    if (file.size > LOGO_MAX) { setLocalError('O logo pode ter até 512 KB.'); return; }
    const reader = new FileReader();
    reader.onload = () => upload.mutate(String(reader.result).split(',')[1] ?? '');
    reader.readAsDataURL(file);
  };
  const error = localError ?? (upload.error ?? remove.error)?.message;
  return (
    <Field label="Logo" hint="PNG com fundo transparente fica melhor. Quadrado ou horizontal, até 512 KB." error={error}>
      <div className="flex items-center gap-4">
        <span className="grid size-16 shrink-0 place-items-center overflow-hidden rounded-xl border border-dashed border-border-strong bg-surface-muted">
          {logoUrl ? <img src={logoUrl} alt="Logo atual" className="size-full object-contain p-1.5" /> : <ImageUp className="size-5 text-fg-subtle" aria-hidden />}
        </span>
        <div className="flex flex-wrap gap-2">
          <input ref={input} type="file" accept={LOGO_TYPES.join(',')} className="sr-only" aria-label="Arquivo do logo"
            onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }} />
          <Button variant="secondary" size="sm" loading={upload.isPending} onClick={() => input.current?.click()}>
            <ImageUp className="size-4" />{logoUrl ? 'Trocar logo' : 'Enviar logo'}
          </Button>
          {logoUrl && <Button variant="ghost" size="sm" loading={remove.isPending} onClick={() => remove.mutate()}><Trash2 className="size-4" />Remover</Button>}
        </div>
      </div>
    </Field>
  );
}

/**
 * Editor da marca, usado pelo painel da clínica (endpoint /clinic/branding) e pelo console
 * (/platform/organizations/:id/branding). O logo é enviado na hora; o resto, ao publicar.
 */
export function BrandEditor({ initial, endpoint, onSaved }: { initial: ClinicBranding; endpoint: string; onSaved?: () => void }) {
  const [form, setForm] = useState({
    displayName: initial.displayName,
    aiDisplayName: initial.aiDisplayName,
    colorPrimary: initial.colorPrimary,
    colorAccent: initial.colorAccent ?? '',
  });
  const [logoUrl, setLogoUrl] = useState(initial.logoUrl);
  const [saved, setSaved] = useState(false);
  const set = (k: keyof typeof form) => (v: string) => { setSaved(false); setForm((f) => ({ ...f, [k]: v })); };
  const valid = form.displayName.trim().length >= 2 && form.aiDisplayName.trim().length >= 2
    && HEX.test(form.colorPrimary) && (form.colorAccent === '' || HEX.test(form.colorAccent));

  const save = useMutation({
    mutationFn: () => api<unknown>(endpoint, {
      method: 'PUT',
      body: { ...form, colorAccent: form.colorAccent || null } satisfies UpdateBranding,
    }),
    onSuccess: () => { setSaved(true); onSaved?.(); },
  });

  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
      <Card>
        <CardHeader title="Marca da clínica" description="Aparece para o paciente no portal, nas mensagens e nos documentos. A marca da plataforma nunca aparece." />
        <form className="grid gap-5 p-5" onSubmit={(e) => { e.preventDefault(); if (valid) save.mutate(); }}>
          <LogoField endpoint={endpoint} logoUrl={logoUrl} onChange={(b) => { setLogoUrl(b.logoUrl); onSaved?.(); }} />
          <Field label="Nome exibido"><Input value={form.displayName} onChange={(e) => set('displayName')(e.target.value)} /></Field>
          <Field label="Nome do assistente virtual" hint="Como o assistente se apresenta no WhatsApp.">
            <Input value={form.aiDisplayName} onChange={(e) => set('aiDisplayName')(e.target.value)} />
          </Field>
          <div className="grid gap-5 sm:grid-cols-2">
            <ColorField label="Cor principal" value={form.colorPrimary} onChange={set('colorPrimary')} />
            <ColorField label="Cor de destaque" value={form.colorAccent} onChange={set('colorAccent')} optional />
          </div>
          <Alert tone="info">Cores de erro, alerta e sucesso são fixas da plataforma, para o paciente reconhecer sempre o mesmo sinal.</Alert>
          {save.error && <Alert tone="danger">{save.error.message}</Alert>}
          {saved && <Alert tone="success">Marca publicada. O portal já mostra a nova versão.</Alert>}
          <div className="flex justify-end border-t border-border pt-5">
            <Button type="submit" disabled={!valid} loading={save.isPending}>Publicar marca</Button>
          </div>
        </form>
      </Card>
      <div>
        <p className="mb-3 text-center text-[13px] font-medium text-fg-muted">Pré-visualização do portal</p>
        <PortalPreview name={form.displayName} primary={form.colorPrimary} logoUrl={logoUrl} />
      </div>
    </div>
  );
}
